import React, { useEffect, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { defaultFeatureAccessForRole, featureOptionsForRole } from '../../constants/featureAccess'
import AdminModal from './AdminModal'

function splitIntoColumns(items, columns = 2) {
  const size = Math.ceil(items.length / columns)
  return Array.from({ length: columns }, (_, i) => items.slice(i * size, (i + 1) * size))
}

function FeatureAccessPicker({ options, featureAccess, onToggle }) {
  const [leftCol, rightCol] = splitIntoColumns(options, 2)
  const renderItem = (opt) => (
    <label key={opt.key} className="admin-permission-item">
      <input
        type="checkbox"
        checked={featureAccess[opt.key] !== false}
        onChange={() => onToggle(opt.key)}
      />
      <span>{opt.label}</span>
    </label>
  )
  return (
    <div className="admin-permission-grid">
      <div className="admin-permission-col">{leftCol.map(renderItem)}</div>
      <div className="admin-permission-col">{rightCol.map(renderItem)}</div>
    </div>
  )
}

function toDateInputValue(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

function InfoRow({ label, value, highlight }) {
  return (
    <div className="admin-kv-row">
      <span className="admin-kv-label">{label}</span>
      <span className={`admin-kv-value${highlight ? ' highlight' : ''}`}>{value || '—'}</span>
    </div>
  )
}

export default function PaymentReviewModal({ orderId, onClose, onApproved }) {
  const [loading, setLoading] = useState(true)
  const [approving, setApproving] = useState(false)
  const [detail, setDetail] = useState(null)
  const [featureAccess, setFeatureAccess] = useState({})
  const [expiresAt, setExpiresAt] = useState('')
  const [planTier, setPlanTier] = useState('tier2')
  const [storageGbLimit, setStorageGbLimit] = useState(10)
  const [contactQuota, setContactQuota] = useState(0)

  useEffect(() => {
    if (!orderId) return
    setLoading(true)
    axios
      .get(`/api/admin/payments/orders/${orderId}`)
      .then((res) => {
        setDetail(res.data)
        const role = res.data?.user?.role || res.data?.table?.role
        const suggested = res.data?.suggested || {}
        setFeatureAccess(suggested.featureAccess || defaultFeatureAccessForRole(role))
        setExpiresAt(toDateInputValue(suggested.expiresAt))
        setPlanTier(suggested.planTier || 'tier2')
        setStorageGbLimit(suggested.storageGbLimit || 10)
        setContactQuota(suggested.contactQuota ?? 50)
      })
      .catch(() => toast.error('Failed to load payment details'))
      .finally(() => setLoading(false))
  }, [orderId])

  const toggleFeature = (key) => {
    setFeatureAccess((prev) => ({ ...prev, [key]: prev[key] === false }))
  }

  const handlePlanChange = (tier) => {
    setPlanTier(tier)
    const opt = detail?.planOptions?.find((p) => p.id === tier)
    if (opt) {
      setStorageGbLimit((prev) => (detail?.entitlements?.purchasedStorageGb > prev ? detail.entitlements.purchasedStorageGb : opt.storageGb))
    }
  }

  const handleApprove = async () => {
    if (!expiresAt) {
      toast.error('Please set membership expiry date')
      return
    }
    setApproving(true)
    try {
      await axios.post(`/api/admin/payments/orders/${orderId}/approve`, {
        featureAccess,
        expiresAt: new Date(`${expiresAt}T23:59:59`).toISOString(),
        periodMonths: detail?.suggested?.periodMonths,
        planTier,
        storageGbLimit: Number(storageGbLimit),
        contactQuota: showContactQuota ? Math.max(0, Number(contactQuota)) : null,
      })
      toast.success('Payment approved — membership activated')
      onApproved?.()
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Approval failed')
    } finally {
      setApproving(false)
    }
  }

  const table = detail?.table
  const ent = detail?.entitlements || {}
  const pkg = ent.packageSummary || {}
  const role = detail?.user?.role || table?.role
  const featureOptions = featureOptionsForRole(role)
  const showContactQuota = role !== 'ServiceProvider'

  return (
    <AdminModal
      open
      wide
      title="Review payment & activate"
      subtitle={table ? `${table.userName} · ${table.companyName}` : undefined}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-btn secondary" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="admin-btn success"
            disabled={loading || approving || !table}
            onClick={handleApprove}
          >
            {approving ? 'Approving…' : 'Approve & activate'}
          </button>
        </>
      )}
    >
          {loading ? (
            <p className="admin-modal-empty">Loading…</p>
          ) : !table ? (
            <p className="admin-modal-empty">Order not found</p>
          ) : (
            <>
              <p className="admin-payment-review-note">
                System-generated payment report — users cannot send this format manually.
              </p>

              <section className="admin-modal-section">
                <h4 className="admin-modal-section-title">User selected package</h4>
                <div className="admin-package-card">
                  <div className="admin-kv-grid">
                    <InfoRow label="Role" value={table.role} />
                    <InfoRow label="Membership tier" value={pkg.tierLabel || table.planTier} highlight />
                    <InfoRow label="Subscription period" value={pkg.subscriptionPeriodMonths ? `${pkg.subscriptionPeriodMonths} months` : table.periodLabel} />
                    <InfoRow
                      label="Storage package"
                      value={pkg.storageLabel || (ent.purchasedStorageGb ? `${ent.purchasedStorageGb} GB` : 'Not included')}
                      highlight={!!ent.purchasedStorageGb}
                    />
                    <InfoRow
                      label="Storage period"
                      value={ent.storagePeriodMonths ? `${ent.storagePeriodMonths} months` : '—'}
                    />
                    <InfoRow label="Amount due" value={table.amountDisplay} highlight />
                  </div>
                  {pkg.selections?.length > 0 && (
                    <ul className="admin-package-selections">
                      {pkg.selections.map((sel) => (
                        <li key={sel}>{sel}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>

              {table.lineItems?.length > 0 && (
                <section className="admin-modal-section">
                  <h4 className="admin-modal-section-title">Line items</h4>
                  <div className="admin-line-items-table-wrap">
                    <table className="admin-line-items-table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Period</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {table.lineItems.map((line, idx) => (
                          <tr key={`${line.label}-${idx}`}>
                            <td>{line.label}</td>
                            <td>{line.periodLabel}</td>
                            <td>{line.amountDisplay}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              <section className="admin-modal-section">
                <h4 className="admin-modal-section-title">Grant entitlements (admin)</h4>
                <div className="admin-form-grid">
                  <label className="admin-form-field">
                    <span className="admin-form-field-label">Plan tier</span>
                    <select className="admin-form-control" value={planTier} onChange={(e) => handlePlanChange(e.target.value)}>
                      {(detail.planOptions || []).map((p) => (
                        <option key={p.id} value={p.id}>{p.label} — {p.storageGb}GB / {p.contactQuota} contacts</option>
                      ))}
                    </select>
                  </label>
                  <label className="admin-form-field">
                    <span className="admin-form-field-label">Files storage quota (GB)</span>
                    <select className="admin-form-control" value={storageGbLimit} onChange={(e) => setStorageGbLimit(Number(e.target.value))}>
                      {(detail.storageOptions || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}{opt.purchased ? ' — user purchased' : ''}
                        </option>
                      ))}
                    </select>
                    <span className="admin-form-hint">Enforced on upload — user cannot exceed this limit</span>
                  </label>
                  {showContactQuota && (
                    <label className="admin-form-field admin-form-field-full">
                      <span className="admin-form-field-label">Monthly private chat contacts</span>
                      <input
                        type="number"
                        min={0}
                        className="admin-form-control"
                        value={contactQuota}
                        onChange={(e) => setContactQuota(Number(e.target.value))}
                      />
                      <span className="admin-form-hint">
                        Default 50 new private-chat peers per month (not tied to the purchased package).
                        Set to <strong>0</strong> to disable private chat for this user (mute).
                      </span>
                    </label>
                  )}
                  <label className="admin-form-field">
                    <span className="admin-form-field-label">Membership expiry</span>
                    <input
                      type="date"
                      className="admin-form-control"
                      value={expiresAt}
                      onChange={(e) => setExpiresAt(e.target.value)}
                    />
                    <span className="admin-form-hint">Auto-filled from subscription period — adjust if needed</span>
                  </label>
                </div>
              </section>

              <section className="admin-modal-section">
                <h4 className="admin-modal-section-title">Module access</h4>
                <FeatureAccessPicker
                  options={featureOptions}
                  featureAccess={featureAccess}
                  onToggle={toggleFeature}
                />
              </section>
            </>
          )}
    </AdminModal>
  )
}

export function PaymentReviewTable({ table }) {
  if (!table) return null
  return (
    <div className="admin-kv-grid compact">
      <InfoRow label="User" value={table.userName} />
      <InfoRow label="Company" value={table.companyName} />
      <InfoRow label="Role" value={table.role} />
      <InfoRow label="Period" value={table.periodLabel} highlight />
      <InfoRow label="Amount" value={table.amountDisplay} highlight />
    </div>
  )
}
